import type { Company, User } from '../../../authentication/types/types.auth';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { Box, IconButton, Tooltip } from '@mui/material';
import Chip from '@mui/material/Chip';
import { forwardRef, useContext, useState } from 'react';
import styled from 'styled-components';
import Button from '@shared/components/Buttons/Button';
import CompanyLogo from '@shared/components/CompanyLogo';
import GicsHierarchy from '@shared/components/GicsHierarchy';
import { UserContext } from '@/shared/contexts/UserContext';
import { useResponsive } from '@shared/hooks/useResponsive';
import { P } from '@shared/styles';
import { layout } from '@shared/theme';
import { gradients } from '@shared/theme/patterns';
import {
  OnboardingAction,
  storeOnboardingIntent,
} from '@shared/utils/onboardingIntent';
import SignUpOrLogInModal from '../../../authentication/components/SignUpOrLogInModal';


interface CompanyProfileHeaderProps {
  company: Company;
  openReviewDialog: (isOpen: boolean) => void;
  followCompany: (user?: User) => void;
  totalReviews: number;
  isSticky?: boolean;
  onSectionClick?: (sectionId: string) => void;
  hasReviews?: boolean;
  reviewIds?: number[];
  hasWorkspace?: boolean;
  isTrialExpired?: boolean;
}

const HeaderWrapper = styled.div<{ $isSticky?: boolean }>`
  background: ${gradients.companyHeader};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md);
  border-radius: 8px;
  border: 1.5px solid rgb(52, 152, 219);
  box-shadow: var(--shadow-md);
  flex-wrap: ${({ $isSticky }) => ($isSticky ? 'nowrap' : 'wrap')};
  gap: var(--spacing-md);
  transition: all 0.2s ease;

  ${({ $isSticky }) =>
    $isSticky
    && `
    position: fixed;
    top: 0;
    left: var(--sidebar-width, 0px);
    right: 0;
    width: calc(100% - var(--sidebar-width, 0px));
    z-index: 1000;
    border-radius: 0;
    border-top: 1.5px solid rgb(52, 152, 219);
    border-bottom: 1.5px solid rgb(52, 152, 219);
    margin: 0;
    padding: var(--spacing-sm) var(--spacing-md);
    height: ${layout.navbarHeight}px;
    overflow: hidden;
    gap: var(--spacing-sm);
  `}

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;

    ${({ $isSticky }) =>
      $isSticky
      && `
      display: none;
    `}
  }
`;

const Header = styled(P)<{ $isSticky?: boolean }>`
  font-size: ${({ $isSticky }) => ($isSticky ? '1.2rem' : '2.5rem')};
  line-height: ${({ $isSticky }) => ($isSticky ? '1.2rem' : '2rem')};
  font-weight: bold;
  color: #555555;
  transition: font-size 0.2s ease;

  @media (max-width: 768px) {
    font-size: 1.75rem;
  }
`;

const TitleImageWrapper = styled.div<{ $isSticky?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ $isSticky }) => ($isSticky ? 'var(--spacing-sm)' : 'var(--spacing-md)')};
  flex: ${({ $isSticky }) => ($isSticky ? '0 0 auto' : '1')};
  min-width: 0;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

const ButtonWrapper = styled.div<{ $isSticky?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ $isSticky }) => ($isSticky ? 'var(--spacing-xs)' : 'var(--spacing-sm)')};
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    align-items: stretch;

    button {
      width: 100%;
    }
  }
`;

const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const SectionNavigation = styled.div<{ $isSticky?: boolean }>`
  display: flex;
  gap: ${({ $isSticky }) => ($isSticky ? 'var(--spacing-xs)' : 'var(--spacing-sm)')};
  margin-left: ${({ $isSticky }) => ($isSticky ? 'auto' : '0')};
  margin-top: ${({ $isSticky }) => ($isSticky ? '0' : 'var(--spacing-sm)')};
  flex-wrap: ${({ $isSticky }) => ($isSticky ? 'nowrap' : 'wrap')};
  flex-shrink: 0;
  min-width: 0;
  overflow: visible;

  @media (max-width: 768px) {
    display: none;
  }
`;

const SectionButton = styled.button<{ $isSticky?: boolean }>`
  background: none;
  border: 1px solid var(--color-brand-primary);
  color: var(--color-brand-primary);
  padding: ${({ $isSticky }) => ($isSticky ? '4px 8px' : 'var(--spacing-xs) var(--spacing-sm)')};
  border-radius: 4px;
  cursor: pointer;
  font-size: ${({ $isSticky }) => ($isSticky ? '0.8rem' : '0.9rem')};
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background-color: var(--color-brand-primary);
    color: white;
  }

  &:focus {
    outline: 2px solid var(--color-brand-primary);
    outline-offset: 2px;
  }
`;
const CompanyProfileHeader = forwardRef<
  HTMLDivElement,
  CompanyProfileHeaderProps
>(
  (
    {
      company,
      openReviewDialog,
      followCompany,
      totalReviews: _totalReviews,
      isSticky = false,
      onSectionClick,
      hasReviews = false,
      reviewIds = [],
      hasWorkspace = false,
      isTrialExpired = false,
    },
    ref,
  ) => {
    const { user } = useContext(UserContext);
    const { isMobile } = useResponsive();
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [pendingAction, setPendingAction] = useState<
      'review' | 'follow' | null
    >(null);

    const showBounty
      = company.createdBy === null
        || company.tags?.some(tag => tag.name === 'Fortune 500');

    const isCompanyFollowed = user?.followedCompanies?.find(
      followedCompany => followedCompany.id === company.id,
    );

    const handleReviewClick = () => {
      if (!user) {
        storeOnboardingIntent({
          action: OnboardingAction.CreateReview,
          returnUrl: window.location.pathname,
          companyId: String(company.id),
          companyName: company.companyName,
        });

        setPendingAction('review');
        setShowAuthModal(true);
        return;
      }
      openReviewDialog(true);
    };

    const handleFollowClick = () => {
      if (!user) {
        storeOnboardingIntent({
          action: OnboardingAction.FollowCompany,
          returnUrl: window.location.pathname,
          companyId: String(company.id),
          companyName: company.companyName,
        });
        setPendingAction('follow');
        setShowAuthModal(true);
        return;
      }
      followCompany();
    };

    const handleAuthSuccess = (authenticatedUser: User) => {
      setShowAuthModal(false);

      if (pendingAction === 'follow') {
        followCompany(authenticatedUser);
      }

      setPendingAction(null);
    };

    return (
      <>
        <HeaderWrapper
          ref={ref}
          className="company-profile-header"
          data-testid="company-profile-header"
          $isSticky={isSticky}
        >
          <TitleImageWrapper $isSticky={isSticky}>
            <CompanyLogo
              companyName={company.companyName || ''}
              companyId={company.id}
              companyUrl={company.url ?? ''}
              alt={`${company.companyName || ''} logo`}
              sx={{
                width: isSticky ? 50 : 100,
                height: isSticky ? 50 : 100,
                transition: 'width 0.2s ease, height 0.2s ease',
              }}
              onClick={() => {}}
            />
            <CompanyInfo className="company-info">
              <Box
                display="flex"
                flexDirection={isSticky ? 'row' : 'column'}
                alignItems={isSticky ? 'center' : 'flex-start'}
                gap={1}
                className="name-and-gics"
              >
                <Header $isSticky={isSticky}>
                  {company.companyName || ''}
                </Header>
                <GicsHierarchy
                  company={company}
                  className="gics"
                  asTooltip={isSticky || isMobile}
                  variant="small"
                />
              </Box>
              {!isSticky && onSectionClick && (
                <SectionNavigation $isSticky={isSticky}>
                  {hasWorkspace && (
                    <SectionButton
                      onClick={() => onSectionClick('my-workspace')}
                      title="Go to My Workspace section"
                    >
                      My Workspace
                    </SectionButton>
                  )}
                  {hasReviews && (
                    <SectionButton
                      onClick={() => onSectionClick('company-profile-ratings')}
                      title="Go to Reviews section"
                    >
                      Deal Reviews
                    </SectionButton>
                  )}
                  {!isTrialExpired && (
                    <SectionButton
                      onClick={() => onSectionClick('threads')}
                      title="Go to Threads section"
                    >
                      Threads
                    </SectionButton>
                  )}
                </SectionNavigation>
              )}
            </CompanyInfo>
          </TitleImageWrapper>

          {isSticky && onSectionClick && (
            <SectionNavigation $isSticky={isSticky}>
              {hasWorkspace && (
                <SectionButton
                  $isSticky={isSticky}
                  onClick={() => onSectionClick('my-workspace')}
                  title="Go to My Workspace section"
                >
                  My Workspace
                </SectionButton>
              )}
              {hasReviews && (
                <SectionButton
                  $isSticky={isSticky}
                  onClick={() => onSectionClick('company-profile-ratings')}
                  title="Go to Reviews section"
                >
                  Deal Reviews
                </SectionButton>
              )}
              {!isTrialExpired && (
                <SectionButton
                  $isSticky={isSticky}
                  onClick={() => onSectionClick('threads')}
                  title="Go to Threads section"
                >
                  Threads
                </SectionButton>
              )}
            </SectionNavigation>
          )}

          <ButtonWrapper $isSticky={isSticky}>
            <Button
              data-testid="leave-review-button"
              size={isSticky ? 'small' : 'medium'}
              onClick={handleReviewClick}
            >
              Add Deal Review
              {showBounty && (
                <Tooltip title="$100 payment available for published deal retros for this company.  There may also be active Intelligence Requests for this company with even higher rewards.">
                  <Chip
                    label="$100"
                    color="success"
                    size={isSticky ? 'small' : 'medium'}
                    sx={{
                      ml: 1,
                      border: '1px solid',
                      borderColor: 'success.dark',
                    }}
                  />
                </Tooltip>
              )}
            </Button>
            <Tooltip title={isCompanyFollowed ? 'Unfollow Company' : 'Follow Company'}>
              <IconButton
                data-testid="follow-company-button"
                aria-label={isCompanyFollowed ? 'Unfollow Company' : 'Follow Company'}
                onClick={handleFollowClick}
                size={isSticky ? 'small' : 'medium'}
                sx={{
                  'color': isCompanyFollowed ? 'warning.main' : 'action.active',
                  '&:hover': { color: 'warning.main' },
                }}
              >
                {isCompanyFollowed
                  ? <StarIcon fontSize={isSticky ? 'small' : 'medium'} />
                  : <StarBorderIcon fontSize={isSticky ? 'small' : 'medium'} />}
              </IconButton>
            </Tooltip>
            {/* <ReviewsCount $isSticky={isSticky}>
            Total Reviews:
            {totalReviews}
          </ReviewsCount> */}
          </ButtonWrapper>
        </HeaderWrapper>

        <SignUpOrLogInModal
          open={showAuthModal}
          onClose={() => {
            setShowAuthModal(false);
            setPendingAction(null);
          }}
          contextMessage={
            pendingAction === 'review'
              ? `Create an account to leave a deal retro for ${company.companyName || ''}`
              : `Create an account to follow ${company.companyName || ''}`
          }
          handleAuthSuccess={handleAuthSuccess}
          initialMode="signup"
        />
      </>
    );
  },
);

CompanyProfileHeader.displayName = 'CompanyProfileHeader';

export default CompanyProfileHeader;
